/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – Date simulate FINALE
// Contaminanți reali din documentul SIMADCA/INFLPR
// Adăugat: Ferme, WQI, Conformitate UE, Factori AI predicții
// ============================================================

import {
  SensorData, SensorHistory, HistoricalPoint,
  Alert, PredictionResult, Report,
  WeatherData, Notification, Farm,
  WaterQualityIndex, ComplianceSummary, Intervention,
} from '../types';

// ─────────────────────────────────────────────────────────────
// HELPERS GENERARE DATE ISTORICE
// ─────────────────────────────────────────────────────────────

function genPoints(
  base: number, variance: number, count: number,
  intervalMs: number, trend = 0
): HistoricalPoint[] {
  const now = Date.now();
  const pts: HistoricalPoint[] = [];
  let v = base;
  for (let i = count - 1; i >= 0; i--) {
    v = Math.max(0, v + trend + (Math.random() - 0.5) * variance * 2);
    pts.push({ timestamp: new Date(now - i * intervalMs).toISOString(), value: parseFloat(v.toFixed(4)) });
  }
  return pts;
}

const genHourly = (base: number, variance: number, hours: number, trend = 0) =>
  genPoints(base, variance, hours, 3_600_000, trend);
const genDaily  = (base: number, variance: number, days: number,  trend = 0) =>
  genPoints(base, variance, days,  86_400_000, trend);

// ─────────────────────────────────────────────────────────────
// FERME
// ─────────────────────────────────────────────────────────────

export const mockFarms: Farm[] = [
  {
    id: 'f01', name: 'Ferma Piscicolă Vest – Timiș',
    location: { lat: 45.748, lng: 21.209, label: 'Timișoara, Timiș' },
    type: 'acvacultura', ownerName: 'SC AquaVest SRL',
    surface: 120, active: true,
    sensorIds: ['s01', 's02', 's03'],
    description: 'Crescătorie de crap și păstrăv. Monitorizare antibiotice și hormoni.',
  },
  {
    id: 'f02', name: 'Ferma Agricolă Est – Iași',
    location: { lat: 47.162, lng: 27.590, label: 'Iași, Moldova' },
    type: 'agricultura', ownerName: 'SC AgroMold SRL',
    surface: 340, active: true,
    sensorIds: ['s04', 's05'],
    description: 'Cultură de cereale. Monitorizare pesticide organofosfatice.',
  },
  {
    id: 'f03', name: 'Ferma Piscicolă Nord – Cluj',
    location: { lat: 46.771, lng: 23.589, label: 'Cluj-Napoca, Cluj' },
    type: 'acvacultura', ownerName: 'SC NordAqua SRL',
    surface: 85, active: true,
    sensorIds: ['s06', 's07', 's08'],
    description: 'Salmonicultură. Monitorizare hormoni și organofosfați.',
  },
  {
    id: 'f04', name: 'Ferma Agricolă Sud – Dolj',
    location: { lat: 44.336, lng: 23.795, label: 'Craiova, Dolj' },
    type: 'agricultura', ownerName: 'SC OlteniAgro SRL',
    surface: 510, active: true,
    sensorIds: ['s09', 's10', 's11'],
    description: 'Cultură de legume și floarea-soarelui. Monitorizare nitrați și pesticide.',
  },
  {
    id: 'f05', name: 'Ferma Piscicolă Delta – Tulcea',
    location: { lat: 45.179, lng: 28.802, label: 'Tulcea, Delta Dunării' },
    type: 'acvacultura', ownerName: 'SC DeltaFish SRL',
    surface: 200, active: true,
    sensorIds: ['s12', 's13'],
    description: 'Acvacultură în zonă protejată. Monitorizare atrazina și pesticide.',
  },
  {
    id: 'f06', name: 'Ferma Silvică – Suceava',
    location: { lat: 47.633, lng: 26.253, label: 'Suceava, Bucovina' },
    type: 'silvicultura', ownerName: 'SC SilvaVerde SRL',
    surface: 800, active: true,
    sensorIds: ['s14', 's15'],
    description: 'Păduri de foioase și conifere. Monitorizare ape de suprafață.',
  },
];

// ─────────────────────────────────────────────────────────────
// 15 SENZORI
// ─────────────────────────────────────────────────────────────

export const mockSensors: SensorData[] = [
  // ── FO-SPR: fibra optică – INFLPR ─────────────────────────
  {
    id: 's01', name: 'FO-SPR Alpha',
    type: 'FO-SPR', parameter: 'Ciprofloxacină',
    value: 3.82, unit: 'µg/L', lod: 2.5, maxAllowed: 5.0,
    status: 'warning', batteryLevel: 72, signalStrength: 88,
    farmName: 'Ferma Piscicolă Vest – Timiș', farmId: 'f01',
    location: { lat: 45.748, lng: 21.209, label: 'Râul Bega – Timișoara' },
    timestamp: new Date(Date.now() - 4 * 60000).toISOString(),
  },
  {
    id: 's02', name: 'FO-SPR Beta',
    type: 'FO-SPR', parameter: 'Eritromicină',
    value: 1.12, unit: 'µg/L', lod: 3.0, maxAllowed: 6.0,
    status: 'online', batteryLevel: 91, signalStrength: 95,
    farmName: 'Ferma Piscicolă Vest – Timiș', farmId: 'f01',
    location: { lat: 45.752, lng: 21.218, label: 'Bazin Nord – Timiș' },
    timestamp: new Date(Date.now() - 2 * 60000).toISOString(),
  },
  {
    id: 's03', name: 'FO-SPR Gamma',
    type: 'FO-SPR', parameter: 'Estradiol',
    value: 1.47, unit: 'ng/L', lod: 1.2, maxAllowed: 2.5,
    status: 'warning', batteryLevel: 64, signalStrength: 79,
    farmName: 'Ferma Piscicolă Vest – Timiș', farmId: 'f01',
    location: { lat: 45.744, lng: 21.201, label: 'Bazin Sud – Timiș' },
    timestamp: new Date(Date.now() - 6 * 60000).toISOString(),
  },
  // ── SAW: acustic – antibiotice ─────────────────────────────
  {
    id: 's04', name: 'SAW Alpha',
    type: 'SAW', parameter: 'Ciprofloxacină',
    value: 6.34, unit: 'µg/L', lod: 2.5, maxAllowed: 5.0,
    status: 'warning', batteryLevel: 38, signalStrength: 62,
    farmName: 'Ferma Agricolă Est – Iași', farmId: 'f02',
    location: { lat: 47.156, lng: 27.582, label: 'Pârâul Nicolina – Iași' },
    timestamp: new Date(Date.now() - 8 * 60000).toISOString(),
  },
  {
    id: 's05', name: 'SAW Beta',
    type: 'SAW', parameter: 'Eritromicină',
    value: 2.91, unit: 'µg/L', lod: 3.0, maxAllowed: 6.0,
    status: 'warning', batteryLevel: 55, signalStrength: 71,
    farmName: 'Ferma Agricolă Est – Iași', farmId: 'f02',
    location: { lat: 47.168, lng: 27.597, label: 'Canal irigații – Iași' },
    timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
  },
  // ── FO-SPR: hormoni estrogeni ──────────────────────────────
  {
    id: 's06', name: 'FO-SPR Delta',
    type: 'FO-SPR', parameter: 'Estradiol',
    value: 0.62, unit: 'ng/L', lod: 1.2, maxAllowed: 2.5,
    status: 'online', batteryLevel: 88, signalStrength: 93,
    farmName: 'Ferma Piscicolă Nord – Cluj', farmId: 'f03',
    location: { lat: 46.771, lng: 23.589, label: 'Râul Someș – Cluj' },
    timestamp: new Date(Date.now() - 3 * 60000).toISOString(),
  },
  {
    id: 's07', name: 'FO-SPR Epsilon',
    type: 'FO-SPR', parameter: 'Organofosfați (OP)',
    value: 0.048, unit: 'mg/L', lod: 0.1, maxAllowed: 0.5,
    status: 'offline', batteryLevel: 4, signalStrength: 0,
    farmName: 'Ferma Piscicolă Nord – Cluj', farmId: 'f03',
    location: { lat: 46.778, lng: 23.598, label: 'Bazin Vest – Cluj' },
    timestamp: new Date(Date.now() - 3 * 3600000).toISOString(),
  },
  // ── Electrochimic SPE: ecrane imprimare ───────────────────
  {
    id: 's08', name: 'SPE-MIP Alpha',
    type: 'Electrochimic', parameter: 'Organofosfați (OP)',
    value: 0.312, unit: 'mg/L', lod: 0.1, maxAllowed: 0.5,
    status: 'warning', batteryLevel: 47, signalStrength: 68,
    farmName: 'Ferma Piscicolă Nord – Cluj', farmId: 'f03',
    location: { lat: 46.765, lng: 23.580, label: 'Bazin Est – Cluj' },
    timestamp: new Date(Date.now() - 7 * 60000).toISOString(),
  },
  {
    id: 's09', name: 'SPE-MIP Beta',
    type: 'Electrochimic', parameter: 'Nitrați',
    value: 35.2, unit: 'mg/L', lod: 50.0, maxAllowed: 50.0,
    status: 'online', batteryLevel: 76, signalStrength: 85,
    farmName: 'Ferma Agricolă Sud – Dolj', farmId: 'f04',
    location: { lat: 44.336, lng: 23.795, label: 'Canal agricol – Craiova' },
    timestamp: new Date(Date.now() - 4 * 60000).toISOString(),
  },
  {
    id: 's10', name: 'SPE-MIP Gamma',
    type: 'Electrochimic', parameter: 'Nitrați',
    value: 42.8, unit: 'mg/L', lod: 50.0, maxAllowed: 50.0,
    status: 'online', batteryLevel: 82, signalStrength: 90,
    farmName: 'Ferma Agricolă Sud – Dolj', farmId: 'f04',
    location: { lat: 44.342, lng: 23.804, label: 'Bazin de irigații – Dolj' },
    timestamp: new Date(Date.now() - 2 * 60000).toISOString(),
  },
  {
    id: 's11', name: 'SPE-MIP Delta',
    type: 'Electrochimic', parameter: 'Nitrați',
    value: 58.7, unit: 'mg/L', lod: 50.0, maxAllowed: 50.0,
    status: 'warning', batteryLevel: 31, signalStrength: 55,
    farmName: 'Ferma Agricolă Sud – Dolj', farmId: 'f04',
    location: { lat: 44.328, lng: 23.788, label: 'Râul Jiu – Craiova' },
    timestamp: new Date(Date.now() - 9 * 60000).toISOString(),
  },
  // ── SAW: pesticide ─────────────────────────────────────────
  {
    id: 's12', name: 'SAW Gamma',
    type: 'SAW', parameter: 'Organofosfați (OP)',
    value: 0.068, unit: 'mg/L', lod: 0.1, maxAllowed: 0.5,
    status: 'online', batteryLevel: 69, signalStrength: 78,
    farmName: 'Ferma Piscicolă Delta – Tulcea', farmId: 'f05',
    location: { lat: 45.179, lng: 28.802, label: 'Canal Delta – Tulcea' },
    timestamp: new Date(Date.now() - 3 * 60000).toISOString(),
  },
  {
    id: 's13', name: 'SPE-MIP Epsilon',
    type: 'Electrochimic', parameter: 'Pesticide (atrazina)',
    value: 0.19, unit: 'µg/L', lod: 0.1, maxAllowed: 0.1,
    status: 'warning', batteryLevel: 23, signalStrength: 44,
    farmName: 'Ferma Piscicolă Delta – Tulcea', farmId: 'f05',
    location: { lat: 45.185, lng: 28.810, label: 'Brațul Sulina – Delta' },
    timestamp: new Date(Date.now() - 11 * 60000).toISOString(),
  },
  // ── Silvicultură – Suceava ────────────────────────────────
  {
    id: 's14', name: 'SAW Delta',
    type: 'SAW', parameter: 'Nitrați',
    value: 12.4, unit: 'mg/L', lod: 50.0, maxAllowed: 50.0,
    status: 'calibrating', batteryLevel: 58, signalStrength: 72,
    farmName: 'Ferma Silvică – Suceava', farmId: 'f06',
    location: { lat: 47.633, lng: 26.253, label: 'Pârâul Sucevița – Suceava' },
    timestamp: new Date(Date.now() - 14 * 60000).toISOString(),
  },
  {
    id: 's15', name: 'FO-SPR Zeta',
    type: 'FO-SPR', parameter: 'Organofosfați (OP)',
    value: 0.021, unit: 'mg/L', lod: 0.1, maxAllowed: 0.5,
    status: 'online', batteryLevel: 94, signalStrength: 97,
    farmName: 'Ferma Silvică – Suceava', farmId: 'f06',
    location: { lat: 47.640, lng: 26.262, label: 'Râul Moldova – Suceava' },
    timestamp: new Date(Date.now() - 1 * 60000).toISOString(),
  },
];

// ─────────────────────────────────────────────────────────────
// ALERTE MOCK
// ─────────────────────────────────────────────────────────────

export const mockAlerts: Alert[] = [
  {
    id: 'al01', sensorId: 's04', sensorName: 'SAW Alpha',
    parameter: 'Ciprofloxacină', location: 'Pârâul Nicolina – Iași',
    value: 6.34, lod: 2.5, unit: 'µg/L', severity: 'critical', status: 'active',
    message: 'Ciprofloxacină depășit critic: 6.34 µg/L (LOD: 2.5, MAX: 5.0). Risc rezistență antimicrobiană.',
    timestamp: new Date(Date.now() - 12 * 60000).toISOString(),
  },
  {
    id: 'al02', sensorId: 's13', sensorName: 'SPE-MIP Epsilon',
    parameter: 'Pesticide (atrazina)', location: 'Brațul Sulina – Delta',
    value: 0.19, lod: 0.1, unit: 'µg/L', severity: 'critical', status: 'active',
    message: 'Atrazina depășit: 0.19 µg/L (limita UE: 0.1 µg/L). Pesticide organoclorurate detectate.',
    timestamp: new Date(Date.now() - 35 * 60000).toISOString(),
  },
  {
    id: 'al03', sensorId: 's01', sensorName: 'FO-SPR Alpha',
    parameter: 'Ciprofloxacină', location: 'Râul Bega – Timișoara',
    value: 3.82, lod: 2.5, unit: 'µg/L', severity: 'high', status: 'active',
    message: 'Ciprofloxacină: 3.82 µg/L. Trend crescător detectat de AI (+0.04/ciclu).',
    timestamp: new Date(Date.now() - 34 * 60000).toISOString(),
  },
  {
    id: 'al04', sensorId: 's11', sensorName: 'SPE-MIP Delta',
    parameter: 'Nitrați', location: 'Râul Jiu – Craiova',
    value: 58.7, lod: 50.0, unit: 'mg/L', severity: 'high', status: 'acknowledged',
    message: 'Nitrați: 58.7 mg/L (limita: 50 mg/L). Posibil runoff agricol.',
    timestamp: new Date(Date.now() - 2 * 3600000).toISOString(),
    acknowledgedBy: 'Ion Popescu',
  },
  {
    id: 'al05', sensorId: 's05', sensorName: 'SAW Beta',
    parameter: 'Eritromicină', location: 'Canal irigații – Iași',
    value: 2.91, lod: 3.0, unit: 'µg/L', severity: 'medium', status: 'acknowledged',
    message: 'Eritromicină aproape de LOD: 2.91 µg/L (LOD: 3.0). Monitorizare intensificată.',
    timestamp: new Date(Date.now() - 4 * 3600000).toISOString(),
    acknowledgedBy: 'Administrator SIMADCA',
  },
  {
    id: 'al06', sensorId: 's08', sensorName: 'SPE-MIP Alpha',
    parameter: 'Organofosfați (OP)', location: 'Bazin Est – Cluj',
    value: 0.312, lod: 0.1, unit: 'mg/L', severity: 'high', status: 'active',
    message: 'Organofosfați: 0.312 mg/L (MAX: 0.5). Depășire LOD de 3.12×.',
    timestamp: new Date(Date.now() - 55 * 60000).toISOString(),
  },
  {
    id: 'al07', sensorId: 's03', sensorName: 'FO-SPR Gamma',
    parameter: 'Estradiol', location: 'Bazin Sud – Timiș',
    value: 1.47, lod: 1.2, unit: 'ng/L', severity: 'medium', status: 'resolved',
    message: 'Estradiol: 1.47 ng/L. Perturbator endocrin detectat.',
    timestamp: new Date(Date.now() - 8 * 3600000).toISOString(),
    resolvedAt: new Date(Date.now() - 6 * 3600000).toISOString(),
  },
  {
    id: 'al08', sensorId: 's07', sensorName: 'FO-SPR Epsilon',
    parameter: 'Comunicare', location: 'Bazin Vest – Cluj',
    value: 0, lod: 0, unit: '', severity: 'high', status: 'active',
    message: 'Senzor offline de 3 ore. Baterie critică (4%). Necesită intervenție urgentă.',
    timestamp: new Date(Date.now() - 3 * 3600000).toISOString(),
  },
];

// ─────────────────────────────────────────────────────────────
// ISTORIC SENZORI
// ─────────────────────────────────────────────────────────────

export const sensorHistory = mockSensors.map(s => ({
  sensorId: s.id,
  data: [
    ...genHourly(s.value * 0.85, s.lod * 0.06, 48, (s.value - s.lod * 0.85) / 100),
    ...genDaily(s.value * 0.80, s.lod * 0.10, 30, (s.value - s.lod * 0.75) / 200),
  ],
}));

// ─────────────────────────────────────────────────────────────
// PREDICȚII AI cu factori explicativi
// ─────────────────────────────────────────────────────────────

function buildForecast(base: number, lod: number, hours = 48): { date: string; actual?: number; predicted: number; confidence: number }[] {
  const now = Date.now();
  const pts = [];
  let v = base;
  for (let i = -24; i <= hours; i++) {
    const isFuture = i > 0;
    v = Math.max(0, v + (Math.random() - 0.46) * lod * 0.08 + lod * 0.002);
    pts.push({
      date: new Date(now + i * 3600000).toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      actual:     isFuture ? undefined : parseFloat(v.toFixed(4)),
      predicted:  parseFloat((v * (isFuture ? 1.05 : 1)).toFixed(4)),
      confidence: isFuture ? parseFloat((lod * 0.04 * (1 + i / 24)).toFixed(4)) : lod * 0.02,
    });
  }
  return pts;
}

export const mockPredictions: PredictionResult[] = [
  {
    sensorId: 's13', parameter: 'Pesticide (atrazina)', unit: 'µg/L',
    riskProbability: 91, riskLevel: 'critical', forecastHours: 48,
    points: buildForecast(0.19, 0.1),
    topFactors: [
      { name: 'Concentrație curentă vs LOD', impact: 42, direction: 'up' },
      { name: 'Trend ascendent 72h', impact: 28, direction: 'up' },
      { name: 'Sezon aplicare pesticide', impact: 15, direction: 'up' },
      { name: 'Precipitații recente (runoff)', impact: 10, direction: 'up' },
      { name: 'Distanță față de câmpuri agricole', impact: 5, direction: 'down' },
    ],
    recommendation: 'Alertă imediată autorităților de mediu. Suspendare utilizare apei pentru irigații. Investigație surse poluare în amonte.',
  },
  {
    sensorId: 's01', parameter: 'Ciprofloxacină', unit: 'µg/L',
    riskProbability: 82, riskLevel: 'high', forecastHours: 48,
    points: buildForecast(3.82, 2.5),
    topFactors: [
      { name: 'Trend ascendent continuu (+0.04/ciclu)', impact: 35, direction: 'up' },
      { name: 'Concentrație > 1.5× LOD', impact: 30, direction: 'up' },
      { name: 'Antibiotice detectate în ferma adiacentă', impact: 18, direction: 'up' },
      { name: 'Debit râu scăzut (diluție redusă)', impact: 12, direction: 'up' },
      { name: 'Sistem de filtrare activ', impact: 5, direction: 'down' },
    ],
    recommendation: 'Reducere imediată a utilizării antibioticelor în fermă. Activare filtrare avansată. Re-calibrare senzor în 24h.',
  },
  {
    sensorId: 's11', parameter: 'Nitrați', unit: 'mg/L',
    riskProbability: 76, riskLevel: 'high', forecastHours: 48,
    points: buildForecast(58.7, 50.0),
    topFactors: [
      { name: 'Depășire limită legală (58.7 > 50 mg/L)', impact: 40, direction: 'up' },
      { name: 'Sezon fertilizare agricolă', impact: 22, direction: 'up' },
      { name: 'Runoff precipitații', impact: 18, direction: 'up' },
      { name: 'Eutrofizare risc ridicat', impact: 15, direction: 'up' },
      { name: 'Capacitate absorbție sol', impact: 5, direction: 'down' },
    ],
    recommendation: 'Notificare DSP și Apele Române. Restricție temporară irigații. Analiză surse fertilizare în bazin hidrografic.',
  },
  {
    sensorId: 's05', parameter: 'Eritromicină', unit: 'µg/L',
    riskProbability: 63, riskLevel: 'high', forecastHours: 36,
    points: buildForecast(2.91, 3.0),
    topFactors: [
      { name: 'Aproape de LOD (97% din limită)', impact: 38, direction: 'up' },
      { name: 'Tratament antibiotic în curs în fermă', impact: 25, direction: 'up' },
      { name: 'Temperatură apă crescută', impact: 20, direction: 'up' },
      { name: 'Debit curs de apă normal', impact: 17, direction: 'down' },
    ],
    recommendation: 'Monitorizare intensificată la 15 minute. Pregătire protocol alertă pentru depășire LOD iminentă.',
  },
  {
    sensorId: 's08', parameter: 'Organofosfați (OP)', unit: 'mg/L',
    riskProbability: 58, riskLevel: 'medium', forecastHours: 36,
    points: buildForecast(0.312, 0.1),
    topFactors: [
      { name: 'Concentrație ridicată relativ (3.12× LOD)', impact: 45, direction: 'up' },
      { name: 'Sezon aplicare pesticide activ', impact: 30, direction: 'up' },
      { name: 'Filtru biologic activ', impact: 25, direction: 'down' },
    ],
    recommendation: 'Verificare surse de organofosfați în amonte. Testare suplimentară probe de apă.',
  },
  {
    sensorId: 's03', parameter: 'Estradiol', unit: 'ng/L',
    riskProbability: 44, riskLevel: 'medium', forecastHours: 24,
    points: buildForecast(1.47, 1.2),
    topFactors: [
      { name: 'Concentrație la 122% din LOD', impact: 50, direction: 'up' },
      { name: 'Perturbator endocrin detectat', impact: 30, direction: 'up' },
      { name: 'Trend stabil în ultimele 24h', impact: 20, direction: 'down' },
    ],
    recommendation: 'Continuare monitorizare. Verificare surse de hormoni (medicamente umane în ape uzate).',
  },
  {
    sensorId: 's02', parameter: 'Eritromicină', unit: 'µg/L',
    riskProbability: 18, riskLevel: 'low', forecastHours: 24,
    points: buildForecast(1.12, 3.0),
    topFactors: [
      { name: 'Concentrație la 37% din LOD', impact: 60, direction: 'down' },
      { name: 'Trend stabil', impact: 40, direction: 'down' },
    ],
    recommendation: 'Situație normală. Menținere monitorizare standard la 30 minute.',
  },
  {
    sensorId: 's06', parameter: 'Estradiol', unit: 'ng/L',
    riskProbability: 12, riskLevel: 'low', forecastHours: 24,
    points: buildForecast(0.62, 1.2),
    topFactors: [
      { name: 'Concentrație la 52% din LOD', impact: 70, direction: 'down' },
      { name: 'Calitate apă bună în bazin', impact: 30, direction: 'down' },
    ],
    recommendation: 'Fără acțiuni necesare. Calibrare programată în 7 zile.',
  },
  {
    sensorId: 's07', parameter: 'Organofosfați (OP)', unit: 'mg/L',
    riskProbability: 8, riskLevel: 'low', forecastHours: 24,
    points: buildForecast(0.048, 0.1),
    topFactors: [
      { name: 'Concentrație la 48% din LOD', impact: 80, direction: 'down' },
      { name: 'Senzor offline – date incomplete', impact: 20, direction: 'up' },
    ],
    recommendation: 'Prioritate intervenție tehnică pentru repornire senzor (baterie descărcată).',
  },
];

// ─────────────────────────────────────────────────────────────
// RAPOARTE
// ─────────────────────────────────────────────────────────────

export const mockReports: Report[] = [
  {
    id: 'r01', title: 'Raport Zilnic – 15 Mar 2026',
    type: 'daily', status: 'generated',
    generatedAt: new Date(Date.now() - 3600000).toISOString(),
    generatedBy: 'Administrator SIMADCA',
    period: '15.03.2026', sensorCount: 15, alertCount: 6, fileSize: '1.4 MB',
    includeCharts: true, includeCompliance: true,
  },
  {
    id: 'r02', title: 'Raport Săptămânal – S10 2026',
    type: 'weekly', status: 'generated',
    generatedAt: new Date(Date.now() - 86400000).toISOString(),
    generatedBy: 'Ion Popescu',
    period: '09.03 – 15.03.2026', sensorCount: 15, alertCount: 14, fileSize: '5.2 MB',
    includeCharts: true, includeCompliance: true,
  },
  {
    id: 'r03', title: 'Raport Lunar – Februarie 2026',
    type: 'monthly', status: 'generated',
    generatedAt: new Date(Date.now() - 14 * 86400000).toISOString(),
    generatedBy: 'Administrator SIMADCA',
    period: 'Februarie 2026', sensorCount: 14, alertCount: 31, fileSize: '13.7 MB',
    includeCharts: true, includeCompliance: true,
  },
  {
    id: 'r04', title: 'Analiză Antibiotice Q1 2026',
    type: 'custom', status: 'generated',
    generatedAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    generatedBy: 'Maria Ionescu',
    period: '01.01 – 15.03.2026', sensorCount: 5, alertCount: 9, fileSize: '3.4 MB',
    selectedSensors: ['s01','s02','s04','s05'],
    selectedParams: ['Ciprofloxacină','Eritromicină'],
    includeCharts: true, includeCompliance: false,
  },
  {
    id: 'r05', title: 'Raport Organofosfați – Zona Oltenia',
    type: 'custom', status: 'generated',
    generatedAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    generatedBy: 'Ion Popescu',
    period: '01.02 – 15.03.2026', sensorCount: 3, alertCount: 5, fileSize: '2.1 MB',
    selectedSensors: ['s08','s12'],
    selectedParams: ['Organofosfați (OP)'],
    includeCharts: true, includeCompliance: true,
  },
  {
    id: 'r06', title: 'Raport Zilnic – 16 Mar 2026',
    type: 'daily', status: 'pending',
    generatedAt: new Date().toISOString(),
    generatedBy: 'Sistem automat',
    period: '16.03.2026', sensorCount: 15, alertCount: 0, fileSize: '–',
  },
];

// ─────────────────────────────────────────────────────────────
// METEO
// ─────────────────────────────────────────────────────────────

export const mockWeather: WeatherData = {
  temperature: 9.4, humidity: 71, pressure: 1016,
  windSpeed: 18, windDirection: 'NV',
  condition: 'Parțial noros', icon: '⛅',
  location: 'Iași, România',
  updatedAt: new Date().toISOString(),
};

// ─────────────────────────────────────────────────────────────
// NOTIFICĂRI
// ─────────────────────────────────────────────────────────────

export const mockNotifications: Notification[] = [
  { id: 'n01', type: 'alert', read: false, title: '🔴 Alertă critică – Ciprofloxacină Iași', message: 'SAW Alpha: 6.34 µg/L (limita: 5.0). Depășire 2.5×.', timestamp: new Date(Date.now() - 12 * 60000).toISOString() },
  { id: 'n02', type: 'alert', read: false, title: '🔴 Alertă critică – Atrazina Delta', message: 'SPE-MIP Epsilon: 0.19 µg/L (limita UE: 0.1). Pesticide detectate.', timestamp: new Date(Date.now() - 35 * 60000).toISOString() },
  { id: 'n03', type: 'alert', read: false, title: '🟠 Alertă – Ciprofloxacină Timișoara', message: 'FO-SPR Alpha: 3.82 µg/L cu trend crescător.', timestamp: new Date(Date.now() - 34 * 60000).toISOString() },
  { id: 'n04', type: 'system', read: false, title: 'Senzor FO-SPR Epsilon offline', message: 'Cluj: niciun semnal de 3 ore. Baterie critică 4%.', timestamp: new Date(Date.now() - 180 * 60000).toISOString() },
  { id: 'n05', type: 'report', read: true, title: 'Raport zilnic generat', message: 'Raportul 15.03.2026 disponibil. 6 alerte raportate.', timestamp: new Date(Date.now() - 3600000).toISOString() },
  { id: 'n06', type: 'alert', read: true, title: 'Alertă rezolvată – Nitrați Dolj', message: 'SPE-MIP Alpha: valorile au revenit sub limita de 50 mg/L.', timestamp: new Date(Date.now() - 2 * 86400000).toISOString() },
];

// ─────────────────────────────────────────────────────────────
// STATISTICI OVERVIEW
// ─────────────────────────────────────────────────────────────

export const dailyAlertStats = Array.from({ length: 30 }, (_, i) => {
  const d = new Date(Date.now() - (29 - i) * 86400000);
  return {
    date:     d.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' }),
    critical: Math.floor(Math.random() * 2),
    high:     Math.floor(Math.random() * 3 + 1),
    medium:   Math.floor(Math.random() * 3),
    low:      Math.floor(Math.random() * 2),
  };
});

export const contaminantStats = [
  { name: 'Ciprofloxacină',   count: 3, color: '#ef4444' },
  { name: 'Eritromicină',     count: 2, color: '#f97316' },
  { name: 'Estradiol',        count: 2, color: '#8b5cf6' },
  { name: 'Organofosfați',    count: 4, color: '#06b6d4' },
  { name: 'Nitrați',          count: 3, color: '#10b981' },
  { name: 'Pesticide (Atra)', count: 1, color: '#f59e0b' },
];

// ─────────────────────────────────────────────────────────────
// WATER QUALITY INDEX per fermă
// ─────────────────────────────────────────────────────────────

export const mockWQI: WaterQualityIndex[] = [
  {
    farmId: 'f01', farmName: 'Ferma Piscicolă Vest – Timiș',
    score: 52, level: 'moderate',
    trend: 'degrading', lastUpdated: new Date().toISOString(),
    complianceStatus: 'warning',
    parameters: [
      { name: 'Ciprofloxacină', value: 3.82, lod: 2.5, maxAllowed: 5.0, unit: 'µg/L', weight: 0.30, euDirective: 'Dir. 2013/39/UE', score: 45 },
      { name: 'Eritromicină',   value: 1.12, lod: 3.0, maxAllowed: 6.0, unit: 'µg/L', weight: 0.25, euDirective: 'Dir. 2013/39/UE', score: 78 },
      { name: 'Estradiol',      value: 1.47, lod: 1.2, maxAllowed: 2.5, unit: 'ng/L', weight: 0.45, euDirective: 'Dir. 2013/39/UE Art.16', score: 41 },
    ],
  },
  {
    farmId: 'f02', farmName: 'Ferma Agricolă Est – Iași',
    score: 28, level: 'poor',
    trend: 'degrading', lastUpdated: new Date().toISOString(),
    complianceStatus: 'non_compliant',
    parameters: [
      { name: 'Ciprofloxacină', value: 6.34, lod: 2.5, maxAllowed: 5.0, unit: 'µg/L', weight: 0.55, euDirective: 'Dir. 2013/39/UE', score: 0 },
      { name: 'Eritromicină',   value: 2.91, lod: 3.0, maxAllowed: 6.0, unit: 'µg/L', weight: 0.45, euDirective: 'Dir. 2013/39/UE', score: 62 },
    ],
  },
  {
    farmId: 'f03', farmName: 'Ferma Piscicolă Nord – Cluj',
    score: 61, level: 'moderate',
    trend: 'stable', lastUpdated: new Date().toISOString(),
    complianceStatus: 'warning',
    parameters: [
      { name: 'Estradiol',      value: 0.62, lod: 1.2, maxAllowed: 2.5, unit: 'ng/L', weight: 0.35, euDirective: 'Dir. 2013/39/UE Art.16', score: 90 },
      { name: 'Organofosfați',  value: 0.312, lod: 0.1, maxAllowed: 0.5, unit: 'mg/L', weight: 0.35, euDirective: 'Reg. 1107/2009/CE', score: 38 },
      { name: 'Organofosfați',  value: 0.048, lod: 0.1, maxAllowed: 0.5, unit: 'mg/L', weight: 0.30, euDirective: 'Reg. 1107/2009/CE', score: 95 },
    ],
  },
  {
    farmId: 'f04', farmName: 'Ferma Agricolă Sud – Dolj',
    score: 45, level: 'moderate',
    trend: 'stable', lastUpdated: new Date().toISOString(),
    complianceStatus: 'warning',
    parameters: [
      { name: 'Nitrați', value: 35.2, lod: 50.0, maxAllowed: 50.0, unit: 'mg/L', weight: 0.34, euDirective: 'Dir. 91/676/CEE (Nitrați)', score: 70 },
      { name: 'Nitrați', value: 42.8, lod: 50.0, maxAllowed: 50.0, unit: 'mg/L', weight: 0.33, euDirective: 'Dir. 91/676/CEE (Nitrați)', score: 57 },
      { name: 'Nitrați', value: 58.7, lod: 50.0, maxAllowed: 50.0, unit: 'mg/L', weight: 0.33, euDirective: 'Dir. 91/676/CEE (Nitrați)', score: 0 },
    ],
  },
  {
    farmId: 'f05', farmName: 'Ferma Piscicolă Delta – Tulcea',
    score: 22, level: 'critical',
    trend: 'degrading', lastUpdated: new Date().toISOString(),
    complianceStatus: 'non_compliant',
    parameters: [
      { name: 'Organofosfați',       value: 0.068, lod: 0.1, maxAllowed: 0.5, unit: 'mg/L', weight: 0.40, euDirective: 'Reg. 1107/2009/CE', score: 86 },
      { name: 'Pesticide (atrazina)', value: 0.19,  lod: 0.1, maxAllowed: 0.1, unit: 'µg/L', weight: 0.60, euDirective: 'Dir. 2000/60/CE Art.16', score: 0 },
    ],
  },
  {
    farmId: 'f06', farmName: 'Ferma Silvică – Suceava',
    score: 88, level: 'good',
    trend: 'improving', lastUpdated: new Date().toISOString(),
    complianceStatus: 'compliant',
    parameters: [
      { name: 'Nitrați',       value: 12.4,  lod: 50.0, maxAllowed: 50.0, unit: 'mg/L', weight: 0.50, euDirective: 'Dir. 91/676/CEE (Nitrați)', score: 95 },
      { name: 'Organofosfați', value: 0.021, lod: 0.1,  maxAllowed: 0.5,  unit: 'mg/L', weight: 0.50, euDirective: 'Reg. 1107/2009/CE', score: 82 },
    ],
  },
];

// ─────────────────────────────────────────────────────────────
// CONFORMITATE UE
// ─────────────────────────────────────────────────────────────

export const mockCompliance: ComplianceSummary = {
  totalChecked: 15,
  compliant: 8,
  warning: 4,
  exceeded: 3,
  lastAudit: new Date(Date.now() - 2 * 3600000).toISOString(),
  items: [
    { parameter: 'Ciprofloxacină', directive: 'Dir. 2013/39/UE – Substanțe prioritare', limit: 5.0, unit: 'µg/L', currentValue: 6.34, status: 'exceeded', exceedancePct: 26.8, sensors: ['s04', 's01'] },
    { parameter: 'Pesticide (atrazina)', directive: 'Dir. 2000/60/CE Art.16 – Cadrul apei', limit: 0.1, unit: 'µg/L', currentValue: 0.19, status: 'exceeded', exceedancePct: 90.0, sensors: ['s13'] },
    { parameter: 'Nitrați', directive: 'Dir. 91/676/CEE – Protecție ape nitrați', limit: 50.0, unit: 'mg/L', currentValue: 58.7, status: 'exceeded', exceedancePct: 17.4, sensors: ['s11'] },
    { parameter: 'Organofosfați (OP)', directive: 'Reg. (CE) 1107/2009 – Pesticide', limit: 0.5, unit: 'mg/L', currentValue: 0.312, status: 'warning', sensors: ['s08'] },
    { parameter: 'Eritromicină', directive: 'Dir. 2013/39/UE – Substanțe prioritare', limit: 6.0, unit: 'µg/L', currentValue: 2.91, status: 'warning', sensors: ['s05'] },
    { parameter: 'Estradiol', directive: 'Dir. 2013/39/UE Art.16 – Perturbatori endocrini', limit: 2.5, unit: 'ng/L', currentValue: 1.47, status: 'warning', sensors: ['s03'] },
    { parameter: 'Organofosfați (OP) – Suceava', directive: 'Reg. (CE) 1107/2009 – Pesticide', limit: 0.5, unit: 'mg/L', currentValue: 0.021, status: 'compliant', sensors: ['s15'] },
    { parameter: 'Nitrați – Dolj Nord', directive: 'Dir. 91/676/CEE – Protecție ape nitrați', limit: 50.0, unit: 'mg/L', currentValue: 35.2, status: 'compliant', sensors: ['s09'] },
    { parameter: 'Estradiol – Cluj', directive: 'Dir. 2013/39/UE Art.16', limit: 2.5, unit: 'ng/L', currentValue: 0.62, status: 'compliant', sensors: ['s06'] },
    { parameter: 'Eritromicină – Timiș', directive: 'Dir. 2013/39/UE', limit: 6.0, unit: 'µg/L', currentValue: 1.12, status: 'compliant', sensors: ['s02'] },
  ],
};

// ─────────────────────────────────────────────────────────────
// MENTENANȚĂ — intervenții mock
// ─────────────────────────────────────────────────────────────

export const mockInterventions: Intervention[] = [
  {
    id: 'i01', sensorId: 's07', sensorName: 'FO-SPR Epsilon',
    type: 'reparatie', status: 'planificata', priority: 'critical', estimatedDuration: 2,
    scheduledAt: new Date(Date.now() + 3600000).toISOString(),
    technician: 'Andrei Marin',
    notes: 'Baterie descărcată (4%). Necesită înlocuire baterie și reconectare comunicații.',
  },
  {
    id: 'i02', sensorId: 's13', sensorName: 'SPE-MIP Epsilon',
    type: 'calibrare', status: 'planificata', priority: 'high', estimatedDuration: 1,
    scheduledAt: new Date(Date.now() + 86400000).toISOString(),
    technician: 'Maria Popa',
    notes: 'Calibrare periodică 30 zile. Verificare strat MIP.',
  },
  {
    id: 'i03', sensorId: 's04', sensorName: 'SAW Alpha',
    type: 'inspectie', status: 'in_curs', priority: 'high', estimatedDuration: 3,
    scheduledAt: new Date(Date.now() - 1800000).toISOString(),
    technician: 'Ion Vasile',
    notes: 'Verificare in situ după alertele critice de Ciprofloxacină.',
  },
  {
    id: 'i04', sensorId: 's11', sensorName: 'SPE-MIP Delta',
    type: 'calibrare', status: 'planificata', priority: 'medium', estimatedDuration: 1,
    scheduledAt: new Date(Date.now() + 2 * 86400000).toISOString(),
    technician: 'Gheorghe Niță',
    notes: 'Calibrare lunară. Nivelul nitraților persistent ridicat.',
  },
  {
    id: 'i05', sensorId: 's01', sensorName: 'FO-SPR Alpha',
    type: 'curatare', status: 'finalizata', priority: 'medium', estimatedDuration: 1,
    scheduledAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    finishedAt:  new Date(Date.now() - 4 * 86400000 - 3600000).toISOString(),
    technician: 'Andrei Marin',
    notes: 'Curățare fibră optică și verificare film Au-Pd. Performanță restaurată.',
  },
  {
    id: 'i06', sensorId: 's08', sensorName: 'SPE-MIP Alpha',
    type: 'inlocuire', status: 'planificata', priority: 'medium', estimatedDuration: 2,
    scheduledAt: new Date(Date.now() + 7 * 86400000).toISOString(),
    technician: 'Maria Popa',
    notes: 'Înlocuire electrod screen-printed după 90 cicluri de măsurare.',
  },
  {
    id: 'i07', sensorId: 's14', sensorName: 'SAW Delta',
    type: 'calibrare', status: 'in_curs', priority: 'low', estimatedDuration: 1,
    scheduledAt: new Date(Date.now() - 3600000).toISOString(),
    technician: 'Ion Vasile',
    notes: 'Calibrare de rutină lunară. Senzor în status calibrare.',
  },
];